import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Store, Utensils, Film, Calendar, MessageSquare, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: "Total Stores", 
      value: stats?.total_stores || 0, 
      icon: Store, 
      color: "from-[#EC1A23] to-[#561D1A]" 
    },
    { 
      title: "Restaurants", 
      value: stats?.total_restaurants || 0, 
      icon: Utensils, 
      color: "from-emerald-500 to-emerald-700" 
    },
    { 
      title: "Movies", 
      value: stats?.total_movies || 0, 
      icon: Film, 
      color: "from-blue-500 to-blue-700" 
    },
    { 
      title: "Total Bookings", 
      value: stats?.total_bookings || 0, 
      icon: Calendar, 
      color: "from-amber-500 to-amber-700" 
    },
    { 
      title: "Pending Bookings", 
      value: stats?.pending_bookings || 0, 
      icon: Clock, 
      color: "from-orange-500 to-orange-700" 
    },
    { 
      title: "Active Chats", 
      value: stats?.active_conversations || 0, 
      icon: MessageSquare, 
      color: "from-purple-500 to-purple-700" 
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Overview of your mall assistant system</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <Card key={stat.title} className="border-border rounded-none hover:border-primary/30 transition-colors" data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                  <p className="text-3xl font-bold font-['Manrope'] mt-2">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 bg-gradient-to-r ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="h-6 w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Booking Trends Chart */}
        <Card className="border-border rounded-none">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">Booking Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.booking_trends || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E2E0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    stroke="#646464"
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    stroke="#646464"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E2E0',
                      borderRadius: 0
                    }}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="#EC1A23" 
                    radius={[0, 0, 0, 0]}
                    name="Bookings"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent Bookings */}
        <Card className="border-border rounded-none">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">Recent Bookings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.recent_bookings?.length > 0 ? (
                stats.recent_bookings.map((booking, index) => (
                  <div 
                    key={booking.id || index}
                    className="flex items-center justify-between p-4 border border-border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 flex items-center justify-center ${
                        booking.booking_type === 'movie' 
                          ? 'bg-blue-100 text-blue-600' 
                          : 'bg-emerald-100 text-emerald-600'
                      }`}>
                        {booking.booking_type === 'movie' ? <Film className="h-5 w-5" /> : <Utensils className="h-5 w-5" />}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{booking.customer_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{booking.reference_id}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium ${
                        booking.status === 'confirmed' 
                          ? 'bg-emerald-100 text-emerald-700'
                          : booking.status === 'pending'
                          ? 'bg-amber-100 text-amber-700'
                          : booking.status === 'cancelled'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {booking.status}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No recent bookings</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
